import React, { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import * as fbq from '@/services/fbpixel';
import { useQuery } from 'react-query';
import { GetSettings } from '@/services/api/settings';
import useAuth from '@/hooks/useAuth';
import { useNotebookModal } from '@/contexts/NotebookModalContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useLoginModal } from '@/contexts/LoginModalContext';
import { useCartDispatch } from '@/contexts/CartContext';
import { addItem, updateItem } from 'CartActions';
import { LanguageTranslations } from '@/config/language';
import ModalHeader from './Notebook/ModalHeader';
import NotebookModalFooter from './Notebook/ModalFooter';
import toast from 'react-hot-toast';
import CustomToast from '../CustomToast';
import MobileViews from './Notebook/MobileViews';
import { GetNotebookDocument } from '@/services/api/personalize/GetDocuments';
import { GetDedicationImage, GetDedicationImageURL } from '@/services/api/personalize/DedicationImage';
import axios from 'axios';
import GlobalLoader from '../GlobalLoader';
import { useTranslation } from 'next-i18next';
import useUserPreferences from '@/hooks/useUserPreferences';
import ConfirmationModal from './PersonalizeModal/ConfirmationModal';
import NotebookDesktopModals from './Notebook/NotebookDesktopModals';
import { getLocalizedValue, categoryHasImage } from '@/utils/notebookHelpers';

const NotebookModal = ({
  setNotebookModal,
  product,
  inputs,
  editFromCart,
}: {
  setNotebookModal: any;
  product: any;
  inputs: string[];
  editFromCart?: any;
}) => {
  const router = useRouter();
  const { t } = useTranslation('personalizeModal');
  const { t: tAlerts } = useTranslation('alerts');
  const { locale } = router;
  const { setPostLoginCallback } = useLoginModal();
  const cartDispatch = useCartDispatch();
  const { productData, updateProductData, updateCoverImage } = useNotebookModal();

  // --- Edit mode flag ---
  const is_edit = productData?.isEdit === '1' ? '1' : (router.query.is_edit ?? '0') as string;

  const replaceName = is_edit === '1'
    ? (product?.replace_name || '')
    : (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en);

  // --- Stable UUID (mount-stable) ---
  const uuid = useMemo(() => {
    if (is_edit === '1') return product?.uuid || uuidv4();
    return router.query.uuid ? (router.query.uuid as string) : uuidv4();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- hasImage: computed synchronously from categories ---
  const hasImage = useMemo(() => {
    const categories = is_edit === '1'
      ? (product?.categories || [])
      : (productData?.product?.categories || []);
    return categories.some(categoryHasImage);
  }, [is_edit, product?.categories, productData?.product?.categories]);

  // --- Form state ---
  const [childName, setChildName] = useState<string>(() =>
    is_edit === '1'
      ? product?.replace_name || ''
      : (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en) || ''
  );

  const [childgender] = useState<string>(() =>
    is_edit === '1'
      ? product?.gender === 'Female' ? 'Female' : 'Male'
      : product?.has_female === true ? 'Female' : 'Male'
  );

  const [inputLanguage, setInputLanguage] = useState<keyof LanguageTranslations>(
    is_edit === '1' ? (product?.language as keyof LanguageTranslations) || 'hebrew' : 'hebrew'
  );

  const [inputValues, setInputValues] = useState<Record<string, any>>({});

  // --- Image state ---
  const [croppedImage, setCroppedImage] = useState<any>(null);
  const [croppedFile, setCroppedFile] = useState<any>(null);
  const [imageSelected, setImageSelected] = useState<string>(() => hasImage ? 'yes' : 'no');
  const [imageLoading, setImageLoading] = useState(false);

  // --- Navigation ---
  const [step, setStep] = useState(is_edit === '1' ? 'review' : 'info');
  const [activeOption, setActiveOption] = useState<string>('name');

  // --- Notebook options ---
  const [bookPageType, setBookPageType] = useState<string | null>(() => {
    if (is_edit === '1') return product?.language ?? productData?.product?.language ?? null;
    return null;
  });

  const pageOptions = [
    { label: t('pageOptionsArabic'), value: 'arabic' },
    { label: t('pageOptionsMath'), value: 'mathematics' },
    { label: t('pageOptionsDrawing'), value: 'drawing' },
    { label: t('pageOptionsHebrew'), value: 'hebrew' },
    { label: t('pageOptionsEnglish'), value: 'english' },
  ];

  const label = pageOptions.find((opt) => opt.value === bookPageType)?.label || '';
  const [bookPageTypeArabic, setBookPageTypeArabic] = useState(is_edit === '0' ? '' : label);
  const [noOfBooks, setNoOfBooks] = useState<number>(is_edit === '0' ? 1 : product?.quantity ?? 1);

  // --- Modal state ---
  const [nameModal, setNameModal] = useState(false);
  const [pageTypeModal, setPageTypeModal] = useState(false);
  const [noOfBooksModal, setNoOfBooksModal] = useState(false);
  const [confirmCart, setConfirmCart] = useState(false);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [loadingProductEdit] = useState(false);

  // --- Options for footer ---
  const [options, setOptions] = useState([
    { id: 'name', label: t('options.name'), value: childName || (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en), completed: false },
    { id: 'pageType', label: t('options.pageType'), value: t('options.pageTypeValuePlaceholder'), completed: false },
    { id: 'noOfBooks', label: t('options.noOfBooks'), value: t('options.noOfBooksValuePlaceholder'), completed: false },
  ]);

  const markOptionCompleted = (id: string, completed: boolean) => {
    setOptions((prev) => prev.map((opt) => (opt.id === id ? { ...opt, completed } : opt)));
  };

  // --- Queries ---
  const preferences = useUserPreferences();
  const { data: settings } = useQuery(
    ['settings', preferences?.countryCode, preferences?.currencyCode],
    () => GetSettings(preferences?.countryCode, preferences?.currencyCode)
  );

  const { data: document, isLoading: isDocumentLoading } = useQuery(
    ['personalize', { slug: is_edit === '0' ? product?.slug : product?.product_slug, name: childName, language: inputLanguage }],
    GetNotebookDocument
  );

  // --- Computed values ---
  const productLanguages = useMemo(
    () => (is_edit === '0' ? product?.languages : productData?.languages) ?? [],
    [is_edit, product?.languages, productData?.languages]
  );

  const SingleBookPrice = useMemo(() => {
    if (is_edit === '1') {
      const p = product?.price || '0';
      return typeof p === 'string' ? parseFloat(p) : (p?.amount || 0);
    }
    return product?.price?.amount || 0;
  }, [is_edit, product?.price]);

  const priceInPersonalize = SingleBookPrice * noOfBooks;

  const currencySymbol = useMemo(() => {
    return is_edit === '1'
      ? product?.product?.price?.symbol || 'ILS'
      : product?.price?.symbol || 'ILS';
  }, [is_edit, product]);

  const noOfBookOptions = [
    { label: `1 ${t('pack')}`, value: 1, image: '/images/one-book.png', price: SingleBookPrice },
    { label: `2 ${t('pack')}`, value: 2, image: '/images/two-book.png', price: SingleBookPrice * 2 },
    { label: `3 ${t('pack')}`, value: 3, image: '/images/three-book.png', price: SingleBookPrice * 3 },
    { label: `4 ${t('pack')}`, value: 4, image: '/images/four-book.png', price: SingleBookPrice * 4 },
    { label: `5 ${t('pack')}`, value: 5, image: '/images/five-book.png', price: SingleBookPrice * 5 },
    { label: `6 ${t('pack')}`, value: 6, image: '/images/six-book.png', price: SingleBookPrice * 6 },
  ];

  // --- Effects ---

  // Initialise inputValues from URL
  useEffect(() => {
    let urlInputs: Record<string, string> = {};
    if (router.query.inputs) {
      try { urlInputs = JSON.parse(router.query.inputs as string); } catch {}
    } else {
      const defName = locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en;
      urlInputs = {
        name: (router.query.name as string) || defName || '',
        first_name: '',
        age: '',
        init: (defName || '').charAt(0),
      };
    }
    const vals: Record<string, any> = {};
    Object.keys(inputs).forEach((key) => { vals[key] = urlInputs[key] || ''; });
    setInputValues(vals);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs]);

  // Load stored dedication image in edit mode
  useEffect(() => {
    if (is_edit !== '1') return;
    const storedKey = localStorage.getItem(`dedicationImageUrl-${uuid}`);
    if (!storedKey) {
      setImageSelected(hasImage ? 'yes' : 'no');
      return;
    }
    setImageSelected('yes');
    setImageLoading(true);
    GetDedicationImage(storedKey)
      .then((response) => {
        const url = response?.data?.url;
        setCroppedImage(url);
        setCroppedFile(url);
      })
      .catch(() => { setCroppedImage(null); setCroppedFile(null); })
      .finally(() => setImageLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_edit, uuid]);

  // Auto-select 'yes' for image in add mode when hasImage is true
  useEffect(() => {
    if (is_edit === '0' && hasImage) setImageSelected('yes');
  }, [is_edit, hasImage]);

  // Mark all options completed in edit mode
  useEffect(() => {
    if (is_edit === '1') {
      markOptionCompleted('name', true);
      markOptionCompleted('pageType', true);
      markOptionCompleted('noOfBooks', true);
    }
    if (is_edit === '0') {
      updateProductData({ languages: product?.languages });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_edit]);

  // Sync options display values
  useEffect(() => {
    setOptions((prev) =>
      prev.map((opt) => {
        if (opt.id === 'name') return { ...opt, value: `${childName}, ${imageSelected === 'yes' ? t('image.added') : t('image.notAdded')}` };
        if (opt.id === 'pageType') return { ...opt, value: bookPageType ? bookPageTypeArabic : t('options.pageTypeValuePlaceholder') };
        if (opt.id === 'noOfBooks') return { ...opt, value: noOfBooks ? `${noOfBooks}  ${t('pack')}` : t('options.noOfBooksValuePlaceholder') };
        return opt;
      })
    );
  }, [childName, imageSelected, bookPageType, noOfBooks, bookPageTypeArabic, t]);

  // Sync cover image to context
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { updateCoverImage(croppedImage); }, [croppedImage]);

  // Clear post-login callback on unmount
  useEffect(() => () => { setPostLoginCallback(null); }, [setPostLoginCallback]);

  // --- Handlers ---

  const handleNextStep = () => {
    if (!childName || childName === '') {
      toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('enterChildNameNotebook')} />, { duration: 2500 });
      return;
    }
    if (step === 'info' || activeOption === 'name') {
      if (imageSelected === 'yes') {
        if (!croppedImage) {
          toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('pictureSelectionError')} />, { duration: 2500 });
          return;
        }
        updateCoverImage(croppedImage);
      }
      markOptionCompleted('name', true);
    }
    if (step === 'edit' && activeOption === 'pageType') markOptionCompleted('pageType', true);
    if (step === 'edit' && activeOption === 'noOfBooks') markOptionCompleted('noOfBooks', true);
    setStep('review');
  };

  const handleSave = () => {
    if (activeOption === 'pageType') markOptionCompleted('pageType', true);
    if (activeOption === 'noOfBooks') markOptionCompleted('noOfBooks', true);
    setStep('review');
  };

  const UpdateValue = (input: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [input]: value }));
  };

  const addToCart = async () => {
    setAddToCartLoading(true);
    try {
      const productBasePrice = SingleBookPrice;
      const total_price = productBasePrice * noOfBooks;
      const itemProductName = getLocalizedValue(product?.product_name, locale || 'ar');
      const itemDemoName = getLocalizedValue(
        product?.demo_name ?? product?.product?.demo_name ?? productData?.product?.demo_name,
        locale || 'ar'
      );
      const itemSlug = product?.slug ?? product?.product_slug ?? productData?.product?.product_slug;
      const itemImage = product?.images?.[0] ?? product?.image ?? product?.product?.image;

      let finalKey: string | undefined;

      if (imageSelected === 'yes') {
        if (!croppedFile) {
          toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('dedicationImageLoadFailed')} />, { duration: 2500 });
          setProductLoading(false);
          return;
        }
        try {
          const { data } = await GetDedicationImageURL(itemSlug);
          if (!data?.uploadUrl || !data?.uploadedKey) throw new Error('Missing upload URL or key');
          await axios.put(data.uploadUrl, croppedFile, {
            headers: { 'Content-Type': croppedFile.type || 'application/octet-stream' },
          });
          finalKey = data.uploadedKey;
          localStorage.setItem(`dedicationImageUrl-${uuid}`, data.uploadedKey);
        } catch {}
      } else {
        const key = `dedicationImageUrl-${uuid}`;
        if (localStorage.getItem(key)) localStorage.removeItem(key);
      }

      const item = {
        product_id: product?.product_id || product?.id,
        replace_name: childName,
        inputs: { name: childName },
        product_name: itemProductName,
        demo_name: itemDemoName,
        default_replace_name: replaceName,
        image: itemImage,
        product_slug: itemSlug,
        price: productBasePrice,
        quantity: noOfBooks,
        total: total_price,
        language: inputLanguage,
        cover_id: null,
        product_type: 2 as const,
        discount: 0,
        uuid,
        dedication_image: finalKey,
      };

      if (is_edit === '1') {
        cartDispatch(updateItem(item));
        toast.custom((t) => <CustomToast type="success" t={t} message={tAlerts('cart_updated')} />);
      } else {
        cartDispatch(addItem(item));
        toast.custom((t) => <CustomToast type="success" t={t} message={tAlerts('cart_added')} />);
      }

      fbq.event('AddToCart', {
        content_name: itemProductName,
        currency: product?.price?.currency || 'ILS',
        value: total_price,
      });

      router.push(`/products?name=${childName}`);
      setNotebookModal(false);
    } catch {
    } finally {
      setAddToCartLoading(false);
      setConfirmCart(false);
    }
  };

  // --- JSX ---
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-white z-[140] flex flex-col text-right"
        key="notebook-modal"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      >
        <ConfirmationModal
          confirmCart={confirmCart}
          is_edit={is_edit}
          addToCart={addToCart}
          addToCartLoading={addToCartLoading}
          setConfirmCart={setConfirmCart}
          childName={childName}
        />

        {imageLoading && <GlobalLoader heading={String(t('loader.image'))} />}
        {loadingProductEdit && <GlobalLoader heading={String(t('loader.edit'))} />}
        {productLoading && <GlobalLoader heading={String(t('loader.product'))} />}

        <ModalHeader
          productData={productData}
          setNotebookModal={setNotebookModal}
          step={step}
          setStep={setStep}
          setActiveOption={setActiveOption}
        />

        <div className="flex-1 overflow-y-auto space-y-6 px-3 py-4 bg-[#FFFCEB] hide-scrollbar">
          <MobileViews
            step={step}
            inputValues={inputValues}
            is_edit={is_edit}
            inputs={inputs}
            setChildName={setChildName}
            UpdateValue={UpdateValue}
            updateProductData={updateProductData}
            handleNextStep={handleNextStep}
            childName={childName}
            setNormalizeModal={setNotebookModal}
            croppedImage={croppedImage}
            imageSelected={imageSelected}
            setCroppedImage={setCroppedImage}
            setImageSelected={setImageSelected}
            data={document}
            product={product}
            setStep={setStep}
            setActiveOption={setActiveOption}
            isDocumentLoading={isDocumentLoading}
            activeOption={activeOption}
            bookPageType={bookPageType}
            setBookPageType={setBookPageType}
            setBookPageTypeArabic={setBookPageTypeArabic}
            noOfBooks={noOfBooks}
            setNoOfBooks={setNoOfBooks}
            inputLanguage={inputLanguage}
            setInputLanguage={setInputLanguage}
            productLangauges={productLanguages}
            setPageTypeModal={setPageTypeModal}
            noOfBookOptions={noOfBookOptions}
            hasImage={hasImage}
            croppedFile={croppedFile}
            setCroppedFile={setCroppedFile}
          />
        </div>

        <NotebookDesktopModals
          pageTypeModal={pageTypeModal}
          setPageTypeModal={setPageTypeModal}
          nameModal={nameModal}
          setNameModal={setNameModal}
          noOfBooksModal={noOfBooksModal}
          setNoOfBooksModal={setNoOfBooksModal}
          is_edit={is_edit}
          pageOptions={pageOptions}
          productLangauges={productLanguages}
          bookPageType={bookPageType}
          setBookPageType={setBookPageType}
          setBookPageTypeArabic={setBookPageTypeArabic}
          setInputLanguage={setInputLanguage}
          markOptionCompleted={markOptionCompleted}
          inputValues={inputValues}
          UpdateValue={UpdateValue}
          setChildName={setChildName}
          hasImage={hasImage}
          croppedImage={croppedImage}
          setCroppedImage={setCroppedImage}
          imageSelected={imageSelected}
          setImageSelected={setImageSelected}
          croppedFile={croppedFile}
          setCroppedFile={setCroppedFile}
          product={product}
          noOfBooks={noOfBooks}
          setNoOfBooks={setNoOfBooks}
          noOfBookOptions={noOfBookOptions}
          currencySymbol={currencySymbol}
        />

        <NotebookModalFooter
          step={step}
          activeOption={activeOption}
          options={options}
          childName={childName}
          is_edit={is_edit}
          priceInPersonalize={priceInPersonalize}
          inputs={inputs}
          inputValues={inputValues}
          settings={settings}
          setStep={setStep}
          setActiveOption={setActiveOption}
          setConfirmCart={setConfirmCart}
          updateProductData={updateProductData}
          handleNextStep={handleNextStep}
          handleSave={handleSave}
          addToCart={addToCart}
          setNormalizeModal={setNotebookModal}
          setPageTypeModal={setPageTypeModal}
          setNoOfBooksModal={setNoOfBooksModal}
          setNameModal={setNameModal}
        />
      </motion.div>
    </AnimatePresence>
  );
};

export default NotebookModal;
